<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import { slide } from 'svelte/transition';
	import Link from 'svelte-navaid/Link.svelte';
	// Stores
	import BetLegStore from '@/Stores/Racing/BetLegStore.js';
	import PreferenceStore from '@/Stores/PreferenceStore.js';
	// Stores
	import Selection from '@/Models/Selection.js';
	// Constants
	import { Products } from '@/Constants/RaceProducts.js';
	import { BetType } from '@/Constants/Bet.js';
	import { UserRatingColours } from '@/Constants/User.js';
	// Components
	import Polling from '@/Components/Polling.svelte';
	import Rating from '@/Partials/User/Rating.svelte';
	import FontIcon from '@/Components/FontIcon.svelte';
	import Pagination from '@/Components/Pagination.svelte';
	import Icon from '@/Components/Icon.svelte';
	// Services
	import ApiService from '@/Services/ApiService';
	import {
		CurrencyFormat,
		getDivisor,
		getUrlParameter,
		NumberFormat,
		paginationBuilder,
		setUrlParameter
	} from '@/Services/Helpers';
	// Common
	import { getBetNetWinAmountForDisplay, getBetWinAmountForDisplay } from '@/Common/Bet.js';

	export let selection;

	let sortBy = {};
	let pageNo = getUrlParameter('page') || 1;
	let pageSize = 50;
	let loading = getLegsForSelection();

	$: $PreferenceStore && getLegsForSelection();
	$: links = $BetLegStore && $BetLegStore.count() && paginationBuilder(BetLegStore.getPagination());
	$: numberOfPages =
		($BetLegStore.count() && BetLegStore.getPagination() && BetLegStore.getPagination().total_pages) ?? 0;

	function changePage(e) {
		if (e) {
			pageNo = e.detail.page;
			loading = getLegsForSelection();
			setUrlParameter('page', pageNo);
		}
	}

	const sort = (column) => {
		if (sortBy.col !== column) {
			sortBy.col = column;
		}

		if (sortBy.order === 'asc') {
			sortBy.order = 'desc';
		} else {
			sortBy.order = 'asc';
		}

		getLegsForSelection();
	};

	function betAmount(bet) {
		return bet.is_each_way ? bet.bet.bet_amount / 2 : bet.bet.bet_amount;
	}

	function betProduct(bet) {
		return bet.product.id === Products.Fixed ? NumberFormat(bet.fixed_price) : bet.product.name;
	}

	function betReturn(bet) {
		return bet.product.id === Products.Fixed
			? CurrencyFormat((betAmount(bet) * bet.fixed_price) / getDivisor() - betAmount(bet))
			: bet.product.name;
	}

	function getLegsForSelection() {
		return BetLegStore.getLegsForSelection(
			selection.id,
			{
				size: pageSize,
				number: pageNo
			},
			{
				col: sortBy.col,
				order: sortBy.order
			}
		);
	}

	function downloadRunnerBets() {
		const racingSelect = new Selection();
		ApiService.downloadFile(
			`${racingSelect.baseUrl()}/racing_selections/${selection.id}/legs/export`,
			`${selection.number} ${selection.name} bets - ${dayjs().format('YYYYMMDDHHmmss')}`
		);
	}
</script>

<Polling pollFunction={getLegsForSelection} frequency={30} />
<div transition:slide|local class="h-72 col-span-full overflow-auto w-full">
	{#await loading}
		<div class="flex items-center justify-center px-1 mt-1 mr-1">Loading...</div>
	{:then}
		<div class="flex flex-col w-full">
			{#if $BetLegStore.sortByDesc('bet.created_at').count() > 0}
				<div class="flex justify-end">
					<button
						class="focus:outline-none btn-primary p-2 rounded m-1.5"
						on:click|preventDefault|stopPropagation={downloadRunnerBets}
					>
						Export as CSV
					</button>
				</div>
			{/if}
			<div class="flex flex-col h-full w-full px-1 text-sm border border-light-grey">
				<table class="min-w-full divide-y divide-gray-300 w-full">
					<thead class="bg-gray-50">
						<tr>
							<th
								scope="col"
								class="px-3 pt-3.5 pb-1 text-left text-sm font-semibold text-gray-900 cursor-pointer"
								on:click|preventDefault|stopPropagation={() => sort('id')}
							>
								<div class="flex flex-row justify-start items-center">
									Ticket #
									{#if sortBy.col == 'id'}
										<span class="ml-3 text-secondary">
											{#if sortBy.order == 'asc'}
												<Icon name="chevron-up" classes="w-3 h-3 ml-1" />
											{:else}
												<Icon name="chevron-down" classes="w-3 h-3 ml-1" />
											{/if}
										</span>
									{/if}
								</div>
							</th>
							<th
								scope="col"
								class="px-3 pt-3.5 pb-1 text-left text-sm font-semibold text-gray-900 cursor-pointer"
								on:click|preventDefault|stopPropagation={() => sort('created_at')}
							>
								<div class="flex flex-row justify-start items-center">
									Date
									{#if sortBy.col == 'created_at'}
										<span class="ml-3 text-secondary">
											{#if sortBy.order == 'asc'}
												<Icon name="chevron-up" classes="w-3 h-3 ml-1" />
											{:else}
												<Icon name="chevron-down" classes="w-3 h-3 ml-1" />
											{/if}
										</span>
									{/if}
								</div>
							</th>
							<th
								scope="col"
								class="px-3 pt-3.5 pb-1 text-left text-sm font-semibold text-gray-900 cursor-pointer"
								on:click|preventDefault|stopPropagation={() => sort('users.user_rating_id')}
							>
								<div class="flex flex-row justify-start items-center">
									Client
									{#if sortBy.col == 'users.user_rating_id'}
										<span class="ml-3 text-secondary">
											{#if sortBy.order === 'asc'}
												<Icon name="chevron-up" classes="w-3 h-3 ml-1" />
											{:else}
												<Icon name="chevron-down" classes="w-3 h-3 ml-1" />
											{/if}
										</span>
									{/if}
								</div>
							</th>
							<th
								scope="col"
								class="px-3 pt-3.5 pb-1 text-left text-sm font-semibold text-gray-900 cursor-pointer"
								on:click|preventDefault|stopPropagation={() => sort('bet_amount')}
							>
								<div class="flex flex-row justify-start items-center">
									Bet
									{#if sortBy.col == 'bet_amount'}
										<span class="ml-3 text-secondary">
											{#if sortBy.order === 'asc'}
												<Icon name="chevron-up" classes="w-3 h-3 ml-1" />
											{:else}
												<Icon name="chevron-down" classes="w-3 h-3 ml-1" />
											{/if}
										</span>
									{/if}
								</div>
							</th>
							<th
								scope="col"
								class="px-3 pt-3.5 pb-1 text-left text-sm font-semibold text-gray-900"
								on:click|preventDefault|stopPropagation={() => sort}
							>
								<div class="flex flex-row justify-start items-center">Type</div>
							</th>
							<th
								scope="col"
								class="px-3 pt-3.5 pb-1 text-left text-sm font-semibold text-gray-900"
								on:click|preventDefault|stopPropagation={() => sort}
							>
								<div class="flex flex-row justify-start items-center">Liability</div>
							</th>
							<th
								scope="col"
								class="px-3 pt-3.5 pb-1 text-left text-sm font-semibold text-gray-900"
								on:click|preventDefault|stopPropagation={() => sort}
							>
								<div class="flex flex-row justify-start items-center">Return</div>
							</th>
							<th
								scope="col"
								class="px-3 pt-3.5 pb-1 text-left text-sm font-semibold text-gray-900"
								on:click|preventDefault|stopPropagation={() => sort}
							>
								<div class="flex flex-row justify-start items-center">Net Win</div>
							</th>
							<th
								scope="col"
								class="px-3 pt-3.5 pb-1 text-left text-sm font-semibold text-gray-900"
								on:click|preventDefault|stopPropagation={() => sort}
							>
								<div class="flex flex-row justify-start items-center">Status</div>
							</th>
						</tr>
					</thead>
					<tbody class="divide-y divide-gray-200 bg-white">
						{#each $BetLegStore.all() as bet (bet.id)}
							<tr class="hover:bg-gray-100">
								<td class="whitespace-nowrap px-3 py-1 text-sm">
									{bet.bet.ticket}
								</td>
								<td class="whitespace-nowrap px-3 py-1 text-sm">
									{dayjs(bet.bet.created_at).format('D/M/YY H:mm')}
								</td>
								<td class="whitespace-nowrap px-3 py-1 text-sm">
									<div
										class="flex flex-row {UserRatingColours[bet.bet.user.rating.id].bg} {UserRatingColours[
											bet.bet.user.rating.id
										].text}"
									>
										<Link
											href="/users/{bet.bet.user.id}"
											class="flex items-center focus:outline-none hover:underline"
											target="_blank"
											title="Open Client Profile"
										>
											{bet.bet.user.pin}/{bet.bet.user.name}
											<Rating className="ml-1" rating={bet.bet.user.rating} />
											<span class="p-1 text-center rounded-md text-tiny"
												>{bet.bet.user.sub_rating ? ' (' + bet.bet.user.sub_rating.name + ') ' : ''}</span
											>
										</Link>
									</div>
								</td>
								<td class="whitespace-nowrap px-3 text-sm">
									<div class="flex flex-row items-center pl-1">
										<span>{CurrencyFormat(betAmount(bet))}</span>
										<div class="mx-0.5 mb-0.5 inline-block align-text-top">@</div>
										<span>{betProduct(bet)}</span>
										{#if bet.bet.is_partially_accepted}
											<div class="ml-2 text-sm text-danger">
												(Partialled from: {CurrencyFormat(bet.bet.requested_bet_amount)})
											</div>
										{/if}
										{#if bet.bet.use_bonus}
											<FontIcon icon={'gift'} className="ml-1.5 text-white bg-gray-500 p-1 rounded-sm text-tiny mr-1" />
										{/if}
										{#if bet.boosted}
											<FontIcon icon={'boost-up'} className="ml-1.5 text-blue-500" />
										{/if}
									</div>
								</td>
								<td class="whitespace-nowrap px-3 py-1 text-sm">
									<div class="flex flex-row items-center pl-1">
										{bet.bet_type.name}
										{#if bet.bet.bet_type.id === BetType.Multi}
											<div class="ml-1 text-xs">{`(L${bet.leg_number})`}</div>
											<div class="font-bold text-blue-500" title="Has Multi">*</div>
										{/if}
									</div>
								</td>
								<td class="whitespace-nowrap px-3 py-1 text-sm">
									{betReturn(bet)}
								</td>
								<td class="whitespace-nowrap px-3 py-1 text-sm">
									{getBetWinAmountForDisplay(bet.bet)}
								</td>
								<td class="whitespace-nowrap px-3 py-1 text-sm">
									{getBetNetWinAmountForDisplay(bet.bet)}
								</td>
								<td class="whitespace-nowrap px-3 py-1 text-sm">
									<div class="flex flex-row items-center pl-1">
										{bet.bet.status.name}
									</div>
								</td>
							</tr>
						{/each}
					</tbody>
				</table>
			</div>
		</div>
		{#if numberOfPages > 1}
			<Pagination {links} on:click={changePage} className="py-4 bg-gray-100 md:sticky md:bottom-0" />
		{/if}
	{/await}
</div>
